import { Box, Button, Typography, Divider, Grid, Link } from '@mui/material';
import { useUIState } from '../context/uiState';
import { SimpleRecorder } from '../components/SimpleRecorder';
import { useEffect, useRef, useState } from 'react';
import { TestNoteTranscriptView } from '../components/TestNoteTranscriptView';
import { useRecordingContext } from '../context/RecordingContext';
import { useUser, useAccessToken } from '@features/user';
import { useNavigate } from 'react-router-dom';
import APIService from '../services/APIService';
import ActionableButtonCard from '@components/ui/cards/ActionableButtonCard';
import { useThemeContext } from '../context/theme/context';
import {
  MicNone as MicNoneIcon,
  ShapeLineOutlined as ShapeLineOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  Web,
} from '@mui/icons-material';
interface TestNoteRecordingViewProps {
  onNext?: () => void;
  subjectLanguage?: string;
}

export const TestNoteRecordingView = ({ onNext, subjectLanguage }: TestNoteRecordingViewProps) => {
  const { state } = useUIState();
  const { platform } = state;
  const navigate = useNavigate();
  const user = useUser();
  const { getAccessToken } = useAccessToken();
  const { brandLanguage } = useThemeContext();
  const { setNoteInformation, cleanUpSession } = useRecordingContext();
  const { refreshTemplatesList, templatesList, userState, updateOnboardingStep } = useUser();

  const [microphonePermissionGranted, setMicrophonePermissionGranted] = useState<boolean>(false);
  const [recordingStopped, setRecordingStopped] = useState<boolean>(false);
  const [noteSubmitted, setNoteSubmitted] = useState<boolean>(false);
  const [submittedNoteId, setSubmittedNoteId] = useState<string>();
  const [showSampleRecorder, setShowSampleRecorder] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const showAdditionaInfo = () => {
    if (recordingStopped) {
      return true;
    } else {
      return false;
    }
  };

  const handleSkip = (navigation: string) => {
    cleanUpSession();
    // Clear the noteTitle and additionalInfo fields
    setNoteInformation(prev => ({
      ...prev,
      noteTitle: '',
      additionalInfo: '',
    }));

    if (navigation && navigation != '') {
      window.location.href = navigation;
    }
    if (onNext) onNext();
  };

  const handleSuccessfulNoteSubmission = async (noteId: string) => {
    await handleCheckFirstExampleNote();
    await APIService.makeAPIPostRequest({
      requestString: '/user/logExampleNoteCreated',
      accessToken: await getAccessToken(),
      body: {
        note_id: noteId,
      },
    });
    setNoteSubmitted(true);
    setSubmittedNoteId(noteId);
    cleanUpSession();
  };

  const handleStopRecording = () => {
    setRecordingStopped(true);
    setNoteInformation(prev => ({
      ...prev,
      templateId: templatesList ? templatesList[0].template_id : 'full',
    }));
  };

  const handleMicrophonePermission = () => {
    setMicrophonePermissionGranted(true);
  };

  const handleDeleteRecording = () => {
    setRecordingStopped(false);
    setNoteSubmitted(false);
  };

  const handleComplete = () => {
    if (submittedNoteId) {
      if (onNext) onNext();
      navigate(`/status/${submittedNoteId}`);
    } else {
      if (onNext) onNext();
    }
  };

  const handleCheckFirstExampleNote = async () => {
    if (userState?.onboardingSteps?.generated_example_note !== true) {
      updateOnboardingStep('generated_example_note', true);
    }
  };

  const getInstructionText = (): string[] => {
    if (!microphonePermissionGranted) {
      return ['First, please grant microphone permission.'];
    } else if (!recordingStopped) {
      return [
        'Record your first JotPsych note by reading the script below and adding creative details along the way.',
        'When you are done, press the stop recording button.',
      ];
    } else if (!noteSubmitted) {
      return [
        'Additional info to add? Just modify the box below and then click the submit recording button.',
      ];
    } else {
      const finalParagraphs = [
        "Congratulations, you're now just seconds away from viewing your first JotPsych note.",
        'During your 10-day free trial, you can record unlimited notes and use all of our premium customization features. Use the button below to view your first note!',
      ];

      if (platform !== 'desktop') {
        finalParagraphs.push(
          'To begin building custom templates, create custom note sections, and further train your AI scribe, please visit our website on your desktop computer.',
        );
      }
      return finalParagraphs;
    }
  };

  const patientScript = [
    {
      speaker: 'Provider',
      text: 'Good morning, John. How are you feeling today?',
    },
    {
      speaker: 'Patient',
      text: "Morning, Doctor. I'm feeling a bit overwhelmed, honestly. I'm having trouble focusing.",
    },
    {
      speaker: 'Provider',
      text: "I understand. Let's talk about what's been going on. Just to start, can you tell me about the medications you've tried in the past and how they've worked for you?",
    },
    {
      speaker: 'Patient',
      text: "Sure. I've tried a few different medications over the years. I was on Adderall for a while, but it made me really jittery and anxious. Then, I tried Ritalin, but it didn't seem to help much with my focus. Recently, I was prescribed Vyvanse, but I haven't noticed any significant improvements.",
    },
    {
      speaker: 'Provider',
      text: "It sounds like you've had a tough time finding the right medication. I appreciate you sharing that with me. I have a few more questions to get a better understanding of your situation. Can you tell me a bit about your background and job?",
    },
    {
      speaker: 'Patient',
      text: "Sure. I'm a software developer. I work mostly from home with my wife, which is nice, but it also makes it hard to stay focused. We live together with her brother right now, and her parents are always visiting, which creates a bit of a hectic home environment. Anyways, I've always struggled with keeping track of tasks and managing my time.",
    },
    {
      speaker: 'Provider',
      text: 'Thank you for that. And how about your sleep? How many hours do you usually get, and do you feel rested when you wake up?',
    },
    {
      speaker: 'Patient',
      text: 'I usually get around 5 to 6 hours of sleep. I have trouble falling asleep, and even when I do sleep, I often wake up feeling tired. My wife also tells me that I snore!',
    },
    {
      speaker: 'Provider',
      text: "Sleep is crucial, and it sounds like that's an area we need to address as well. Based on what you've told me, I think we should try a different approach. I'd like to suggest a non-stimulant medication called Strattera. It works differently from the stimulant medications you've tried and might be a better fit for you.",
    },
    {
      speaker: 'Patient',
      text: "I've heard of Strattera, but I don't know much about it. How does it work?",
    },
    {
      speaker: 'Provider',
      text: "Strattera, or atomoxetine, works by increasing the levels of norepinephrine in the brain, which can help with attention and focus. It's not a stimulant, so it doesn't have the same risk of causing jitteriness or anxiety. We'll start with a low dose of 40mg per day and see how you respond.",
    },
    {
      speaker: 'Patient',
      text: "That sounds promising. I'm willing to give it a try.",
    },
    {
      speaker: 'Provider',
      text: "Great. We'll monitor your progress closely and make any necessary adjustments. Also, I'd recommend working on a consistent sleep schedule and perhaps some behavioral strategies, like mindfulness practice, to help with your focus. Let's set up a follow-up appointment in a few weeks to see how you're doing.",
    },
    {
      speaker: 'Patient',
      text: "Oh, I just remembered, I was also on Concerta for a while, but it didn't seem to do much for me either.",
    },
    {
      speaker: 'Provider',
      text: "Thank you for mentioning that. It's important to have all the details. We'll still go ahead with Strattera and see how it works for you. Do you want to meet two weeks from now? How's Thursday?",
    },
    { speaker: 'Patient', text: 'Thursday works for me, thank you!' },
    {
      speaker: 'Provider',
      text: "You're welcome. We'll find a solution that works for you. Take care, and I'll see you at our next appointment.",
    },
    { speaker: 'Patient', text: "Thanks, I'll see you then." },
  ];

  const clientScript = [
    {
      speaker: 'Provider',
      text: 'Good afternoon, John. How are you doing today?',
    },
    {
      speaker: 'Client',
      text: "Hi there. I've been really anxious lately, and it's starting to affect my daily life.",
    },
    {
      speaker: 'Provider',
      text: "I'm sorry to hear that. Let's talk more about your anxiety. Can you describe some of the specific situations where you feel most anxious?",
    },
    {
      speaker: 'Client',
      text: "It mostly happens at work. I have a lot of deadlines, and I constantly feel like I'm not going to meet them. Sometimes, even small tasks seem overwhelming. I also get really anxious in social situations, like meetings or gatherings, even with people I know well.",
    },
    {
      speaker: 'Provider',
      text: "It sounds like you're dealing with both performance anxiety and social anxiety. Have you noticed any physical symptoms that accompany your anxiety?",
    },
    {
      speaker: 'Client',
      text: "Yes, I often feel my heart racing, I start sweating, and I sometimes get headaches. It's really hard to focus on anything when I'm feeling that way.",
    },
    {
      speaker: 'Provider',
      text: "Those physical symptoms are common with anxiety. It's important to address both the physical and mental aspects. Have you tried any methods to manage your anxiety so far?",
    },
    {
      speaker: 'Client',
      text: "I've tried deep breathing exercises, but they don't always help. I also tried meditation, but I find it hard to stick with it.",
    },
    {
      speaker: 'Provider',
      text: "It's good that you've tried those techniques. They can be very effective, but they do require practice. Along with those, we can also consider pharmacological intervention. One medication that might help is Sertraline, which is commonly used to treat anxiety disorders. However, I'd like to focus on some home exercises that might help you manage your anxiety better.",
    },
    {
      speaker: 'Client',
      text: 'Okay, what kind of exercises?',
    },
    {
      speaker: 'Provider',
      text: "Let's start with cognitive-behavioral techniques. One effective method is journaling. Writing down your thoughts and feelings can help you identify patterns and triggers for your anxiety. You can start by noting when you feel anxious and what was happening at the time.",
    },
    {
      speaker: 'Client',
      text: 'That sounds doable. I know that my sister did something like that when she was feeling depressed a few years ago. We used to live together and I saw that it was really hard for her. Is there anything else?',
    },
    {
      speaker: 'Provider',
      text: "We could try another technique called progressive muscle relaxation. This involves tensing and then slowly relaxing different muscle groups in your body. It's a way to release physical tension and can be very calming. Additionally, regular physical exercise, like walking or yoga, can significantly reduce anxiety levels. Are you regularly active?",
    },
    {
      speaker: 'Client',
      text: "I'm not very active right now, not since moving out on my own. But that sounds like a good idea, I'll give those a shot.",
    },
    {
      speaker: 'Provider',
      text: "Great. Consistency is key with these practices. We can also explore mindfulness techniques. There are many apps that offer guided mindfulness sessions which can help you stay present and reduce anxiety, but let's stick to the first two for now and not get ahead of ourselves.",
    },
    {
      speaker: 'Client',
      text: "I've heard about mindfulness apps, but I agree, I want to just start slowly.",
    },
    {
      speaker: 'Provider',
      text: "Excellent. Let's plan to meet again in a few weeks to see how you're doing and adjust our approach as needed. In the meantime, keep track of your anxiety levels and try the techniques we've discussed.",
    },
    {
      speaker: 'Client',
      text: 'Thanks again, I appreciate your help.',
    },
    {
      speaker: 'Provider',
      text: "You're welcome, John. We'll work through this together. Take care, and I'll see you at our next appointment. Next week same time?",
    },
    {
      speaker: 'Client',
      text: "That sounds good. Thanks, I'll see you then.",
    },
  ];

  const setSimpleDefaults = () => {
    if (subjectLanguage === 'client') {
      setNoteInformation(prev => ({
        ...prev,
        noteTitle: 'John Doe, Anxiety Intake',
        additionalInfo: 'Intake Appt, Dx: Moderate Anxiety, GAD-7: 14',
      }));
    } else {
      setNoteInformation(prev => ({
        ...prev,
        noteTitle: 'John Doe, ADHD Intake',
        additionalInfo: 'Intake Appt, Dx: ADHD, CAARS T-score: 75',
      }));
    }
  };

  useEffect(() => {
    setSimpleDefaults();
    refreshTemplatesList();
  }, []);

  // Effect for scrolling to top when status changes
  useEffect(() => {
    if (containerRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [microphonePermissionGranted, recordingStopped, noteSubmitted]);

  const CustomTemplateIcon = () => {
    return (
      <Box sx={{ fontSize: 35 }}>
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.62708 32.1304C3.50319 32.2229 3.37611 32.2813 3.24583 32.3054C3.11556 32.3296 2.98236 32.3346 2.84625 32.3204C2.69153 32.2793 2.56708 32.2083 2.47292 32.1075C2.37847 32.0067 2.30556 31.89 2.25417 31.7575C2.14944 31.5311 2.14681 31.2969 2.24625 31.055C2.34569 30.8133 2.51792 30.6199 2.76292 30.4746C3.18014 30.089 3.67569 29.866 4.24958 29.8054C4.82319 29.7449 5.38569 29.7499 5.93708 29.8204C6.28181 29.8554 6.57764 29.8692 6.82458 29.8617C7.07181 29.8542 7.29014 29.7379 7.47958 29.5129C7.68708 29.1932 7.79278 28.839 7.79667 28.4504C7.80056 28.0618 7.80778 27.6696 7.81833 27.2738C7.79917 26.6654 7.86056 26.074 8.0025 25.4996C8.14472 24.9251 8.45375 24.4446 8.92958 24.0579C9.34681 23.6724 9.84375 23.4493 10.4204 23.3887C10.9968 23.3282 11.5607 23.3332 12.1121 23.4038C12.4499 23.4388 12.7482 23.4525 13.0071 23.445C13.266 23.4375 13.4901 23.3213 13.6796 23.0963C13.8868 22.7765 13.9868 22.4224 13.9796 22.0338C13.9726 21.6451 13.9744 21.2529 13.985 20.8571C13.9658 20.2488 14.0272 19.6574 14.1692 19.0829C14.3114 18.5085 14.6204 18.0279 15.0963 17.6413C15.5135 17.2557 16.0104 17.0354 16.5871 16.9804C17.1635 16.9251 17.7274 16.9328 18.2788 17.0033C18.6165 17.0381 18.9149 17.049 19.1738 17.0363C19.4326 17.0235 19.6568 16.9046 19.8463 16.6796C20.0535 16.3599 20.155 16.0057 20.1508 15.6171C20.1464 15.2285 20.146 14.8363 20.1496 14.4404C20.1374 13.8135 20.2079 13.2175 20.3612 12.6525C20.5143 12.0872 20.8288 11.6113 21.3046 11.2246C21.7218 10.839 22.2188 10.6188 22.7954 10.5638C23.3718 10.5085 23.9357 10.5161 24.4871 10.5867C24.8249 10.6214 25.1232 10.6324 25.3821 10.6196C25.641 10.6068 25.8651 10.4879 26.0546 10.2629C26.2618 9.94319 26.3633 9.58903 26.3592 9.20042C26.3547 8.81181 26.3543 8.41958 26.3579 8.02375C26.3457 7.39681 26.4163 6.80347 26.5696 6.24375C26.7226 5.68375 27.0371 5.20514 27.5129 4.80792C27.9229 4.41097 28.4181 4.18639 28.9983 4.13417C29.5783 4.08167 30.144 4.09069 30.6954 4.16125C31.0543 4.17625 31.3944 4.19139 31.7158 4.20667C32.0369 4.22195 32.3236 4.14055 32.5758 3.9625C32.7708 3.83361 32.9832 3.80111 33.2129 3.865C33.4426 3.92861 33.6203 4.05444 33.7458 4.2425C33.8506 4.46889 33.8532 4.70445 33.7538 4.94917C33.6543 5.19389 33.4821 5.38889 33.2371 5.53417C32.8271 5.93083 32.3319 6.15945 31.7517 6.22C31.1717 6.28056 30.606 6.27556 30.0546 6.205C29.7168 6.17028 29.4185 6.15931 29.1596 6.17208C28.9007 6.18486 28.6765 6.30375 28.4871 6.52875C28.2799 6.84847 28.1783 7.20264 28.1825 7.59125C28.1869 7.97986 28.1874 8.37208 28.1838 8.76792C28.196 9.39486 28.1254 9.99084 27.9721 10.5558C27.819 11.1211 27.5046 11.5971 27.0288 11.9837C26.6115 12.3693 26.116 12.5896 25.5421 12.6446C24.9685 12.6999 24.406 12.6922 23.8546 12.6217C23.5099 12.5869 23.2157 12.576 22.9721 12.5888C22.7285 12.6015 22.5085 12.7204 22.3121 12.9454C22.1046 13.2651 21.9989 13.6193 21.995 14.0079C21.9911 14.3965 21.9839 14.7888 21.9733 15.1846C21.9925 15.7943 21.9338 16.3861 21.7971 16.96C21.6604 17.5336 21.3542 18.0138 20.8783 18.4004C20.4575 18.786 19.9583 19.0078 19.3808 19.0658C18.8036 19.1239 18.2393 19.1176 17.6879 19.0471C17.354 19.0049 17.0599 18.9988 16.8054 19.0288C16.5512 19.0588 16.3312 19.1844 16.1454 19.4058C15.9487 19.715 15.8458 20.0592 15.8367 20.4383C15.8272 20.8178 15.8172 21.2054 15.8067 21.6012C15.8258 22.2096 15.7629 22.801 15.6179 23.3754C15.4732 23.9499 15.1629 24.4304 14.6871 24.8171C14.2768 25.214 13.7775 25.4412 13.1892 25.4987C12.6008 25.5565 12.031 25.5501 11.4796 25.4796C11.1457 25.4376 10.8539 25.4263 10.6042 25.4454C10.3544 25.4646 10.1349 25.5903 9.94542 25.8225C9.74875 26.1317 9.65 26.4758 9.64917 26.855C9.64806 27.2344 9.6457 27.6221 9.64208 28.0179C9.65431 28.6449 9.58375 29.2408 9.43042 29.8058C9.27736 30.3711 8.96292 30.8471 8.48708 31.2337C8.06986 31.6193 7.57431 31.8438 7.00042 31.9071C6.42681 31.9704 5.86431 31.9668 5.31292 31.8963C4.97903 31.8543 4.67083 31.8326 4.38833 31.8313C4.10583 31.8299 3.85208 31.9296 3.62708 32.1304ZM8 15.5C5.92944 15.5 4.16181 14.7676 2.69708 13.3029C1.23236 11.8382 0.5 10.0706 0.5 8C0.5 5.90167 1.23236 4.12708 2.69708 2.67625C4.16181 1.22542 5.92944 0.5 8 0.5C10.0983 0.5 11.8729 1.22542 13.3237 2.67625C14.7746 4.12708 15.5 5.90167 15.5 8C15.5 10.0706 14.7746 11.8382 13.3237 13.3029C11.8729 14.7676 10.0983 15.5 8 15.5ZM8 13.4058C9.52194 13.4058 10.8035 12.8785 11.8446 11.8238C12.8854 10.7688 13.4058 9.49417 13.4058 8C13.4058 6.47806 12.8854 5.19653 11.8446 4.15542C10.8035 3.11458 9.52194 2.59417 8 2.59417C6.50583 2.59417 5.23125 3.11458 4.17625 4.15542C3.12153 5.19653 2.59417 6.47806 2.59417 8C2.59417 9.49417 3.12153 10.7688 4.17625 11.8238C5.23125 12.8785 6.50583 13.4058 8 13.4058ZM24.7738 35.5C24.0549 35.5 23.4407 35.2451 22.9312 34.7354C22.4215 34.226 22.1667 33.6118 22.1667 32.8929V24.7738C22.1667 24.0549 22.4215 23.4407 22.9312 22.9312C23.4407 22.4215 24.0549 22.1667 24.7738 22.1667H32.8929C33.6118 22.1667 34.226 22.4215 34.7354 22.9312C35.2451 23.4407 35.5 24.0549 35.5 24.7738V32.8929C35.5 33.6118 35.2451 34.226 34.7354 34.7354C34.226 35.2451 33.6118 35.5 32.8929 35.5H24.7738ZM24.7738 33.4058H32.8929C33.0426 33.4058 33.1656 33.3578 33.2617 33.2617C33.3578 33.1656 33.4058 33.0426 33.4058 32.8929V24.7738C33.4058 24.624 33.3578 24.5011 33.2617 24.405C33.1656 24.3089 33.0426 24.2608 32.8929 24.2608H24.7738C24.624 24.2608 24.5011 24.3089 24.405 24.405C24.3089 24.5011 24.2608 24.624 24.2608 24.7738V32.8929C24.2608 33.0426 24.3089 33.1656 24.405 33.2617C24.5011 33.3578 24.624 33.4058 24.7738 33.4058Z"
            fill="#36229E"
          />
        </svg>
      </Box>
    );
  };
  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        component="div"
        fontWeight={700}
        align="center"
        sx={{ mb: 4 }}
      >
        Let's start using {brandLanguage?.brandName}!
      </Typography>

      {!showSampleRecorder && (
        <>
          <Typography variant="body1" sx={{ mb: 4 }}>
            The first step is to record an encounter note. If you can't record right now, you can
            still select one of our pre-written transcripts, and even customize it to match your
            practice and {subjectLanguage} population.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-evenly',
            }}
            gap={2}
          >
            <Button
              variant="contained"
              startIcon={<MicNoneIcon sx={{ fontSize: 30 }} />}
              onClick={() => handleSkip('/first-encounter')}
              size="large"
              sx={{
                py: 2,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
              }}
            >
              Create my first note
            </Button>
          </Box>
        </>
      )}
      {/* <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        {showSampleRecorder ? (
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => setShowSampleRecorder(false)}
          >
            Back
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setShowSampleRecorder(true)}
          >
            Show Sample Recorder
          </Button>
        )}
      </Box> */}
      {showSampleRecorder && (
        <Box ref={containerRef} sx={{ textAlign: 'center' }}>
          {!microphonePermissionGranted && (
            <Typography variant="h5" gutterBottom>
              Let's record our first mock encounter!
            </Typography>
          )}

          {getInstructionText().map((paragraph, index) => (
            <Typography key={index} variant="body1" sx={{ mt: 1 }}>
              {paragraph}
            </Typography>
          ))}

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              {noteSubmitted ? (
                <></>
              ) : (
                <Box
                  sx={{
                    border: '1px dashed',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <SimpleRecorder
                    onSuccessfulSubmit={handleSuccessfulNoteSubmission}
                    onStopRecording={handleStopRecording}
                    onReceiveAudioPermission={handleMicrophonePermission}
                    onDeleteRecording={handleDeleteRecording}
                    showNoteTitle={showAdditionaInfo()}
                    showAdditionalInfo={showAdditionaInfo()}
                    showAudioStream={microphonePermissionGranted}
                  />
                </Box>
              )}
            </Grid>

            {microphonePermissionGranted && !recordingStopped && (
              <Grid item xs={12} md={6}>
                <Box sx={{ height: { md: '100%' }, overflowY: 'auto' }}>
                  <TestNoteTranscriptView
                    transcript={subjectLanguage === 'client' ? clientScript : patientScript}
                  />
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 4 }}>
            {noteSubmitted && (
              <Box sx={{ mt: 4 }}>
                <Button variant="contained" onClick={handleComplete}>
                  Let's get started!
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* {!noteSubmitted && onNext && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Link onClick={() => handleSkip("")} underline="hover">
              Explore on my own
            </Link>
          </Box>
        )} */}
    </Box>
  );
};
